/**
 * /live/:live_session_id — Live Session join page (Jitsi iframe).
 * Free → just signup + join; Paid → Razorpay → signup-paid/verify → join.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Users, Calendar, IndianRupee } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

export default function LiveJoin() {
  const navigate = useNavigate();
  const { live_session_id } = useParams();
  const [session, setSession] = useState(null);
  const [joinUrl, setJoinUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const iframeRef = useRef(null);

  const load = useCallback(() => {
    apiClient.get(`/live/sessions/${live_session_id}`).then((r) => setSession(r.data));
  }, [live_session_id]);
  useEffect(() => { load(); }, [load]);

  // Razorpay loader (same pattern as ArenaPlans)
  useEffect(() => {
    if (window.Razorpay) return;
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  if (!session) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg-base, #ECEAE6)" }}>Loading…</div>;

  const signupFree = async () => {
    setBusy(true);
    try { await apiClient.post(`/live/sessions/${live_session_id}/signup`); toast.success("Signed up!"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const signupPaid = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/live/sessions/${live_session_id}/signup-paid/order`);
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "FitFrik Live", description: session.title,
        handler: async (resp) => {
          try {
            await apiClient.post(`/live/sessions/${live_session_id}/signup-paid/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment confirmed — you're in!");
            load();
          } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
        },
        theme: { color: "#A52121" },
      });
      rzp.open();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const join = async () => {
    try {
      const { data } = await apiClient.get(`/live/sessions/${live_session_id}/join`);
      setJoinUrl(`https://${data.domain}/${data.room}#userInfo.displayName="${encodeURIComponent(data.user_info.displayName)}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot join"); }
  };

  if (joinUrl) {
    return (
      <div data-testid="live-jitsi-frame" style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <header style={{ padding: 10, background: "#0C0C0B", color: "white", display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => { setJoinUrl(null); load(); }} style={{ background: "transparent", border: "none", color: "white" }}><ChevronLeft size={20} /></button>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{session.title} · LIVE</span>
        </header>
        <iframe ref={iframeRef} title="live-session" src={joinUrl} allow="camera; microphone; fullscreen; display-capture; autoplay" style={{ flex: 1, border: 0 }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="live-detail">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center px-4 py-3 gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}><ChevronLeft size={22} /></button>
          <span style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800 }}>Live session</span>
          {session.status === "live" && <span style={{ marginLeft: "auto", padding: "3px 8px", background: "#A52121", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 100, textTransform: "uppercase" }}>Live now</span>}
        </div>
      </header>
      <main className="max-w-md mx-auto px-5 pt-4">
        <div style={{ padding: 16, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 14 }}>
          <div style={{ fontFamily: "Barlow Condensed", fontSize: 24, fontWeight: 900, lineHeight: 1.05 }}>{session.title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12, color: "var(--text-secondary, #4A4845)" }}>
            {session.trainer_avatar ? <img src={session.trainer_avatar} alt="" width={20} height={20} style={{ borderRadius: "50%" }} /> : null}
            <span>by {session.trainer_name}</span>
          </div>
          {session.description && <p style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)", marginTop: 10 }}>{session.description}</p>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14, fontSize: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-tertiary, #8A8884)" }}><Calendar size={12} /> {new Date(session.scheduled_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-tertiary, #8A8884)" }}><Users size={12} /> {session.current_participants}/{session.max_participants}</div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          {session.my_signup ? (
            <button onClick={join} disabled={session.status === "completed"} data-testid="live-join-btn"
              style={{ width: "100%", height: 52, background: "#A52121", color: "white", border: "none", borderRadius: 14, fontFamily: "Barlow", fontSize: 16, fontWeight: 800 }}>
              {session.status === "live" ? "Join now" : session.status === "completed" ? "Session ended" : "Open lobby (room opens at scheduled time)"}
            </button>
          ) : session.type === "paid" ? (
            <button onClick={signupPaid} disabled={busy} data-testid="live-pay-signup-btn"
              style={{ width: "100%", height: 52, background: "#0C0C0B", color: "white", border: "none", borderRadius: 14, fontFamily: "Barlow", fontSize: 16, fontWeight: 800, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
              <IndianRupee size={14} /> Pay ₹{session.price_inr} & Sign up
            </button>
          ) : (
            <button onClick={signupFree} disabled={busy} data-testid="live-free-signup-btn"
              style={{ width: "100%", height: 52, background: "#0C0C0B", color: "white", border: "none", borderRadius: 14, fontFamily: "Barlow", fontSize: 16, fontWeight: 800 }}>
              {busy ? "Signing up…" : "Sign up — Free"}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
